<template>
    <div class="wrapper">
        <Sidebar />
        <div class="main-panel" id="main-panel">
            <Header />
            <div class="panel-header panel-header-sm">
                <canvas id="bigDashboardChart"></canvas>
            </div>
            <div class="content">
                <Loading v-if="loading === true" />
                <div class="row">
                    <div class="card">
                        <div class="card-header">
                            <div class="row">
                                <div class="col-6">
                                    <h5 class="title">Edit Artwork</h5>
                                </div>
                                <div v-if="isSuperAdmin === true" class="col-6">
                                    <a class="btn btn-danger text-white pull-right" style="font-size: 14px;" @click="deleteListing(listing.id)"><i class="fa fa-trash mr-2"></i>Delete Artwork</a>
                                    <a v-if="listing.verified === 1" class="btn btn-danger btn-orange text-black pull-right" style="font-size: 14px;" @click="unverify(listing.id)">Unverify Artwork</a>
                                    <a v-if="listing.approved === 1" class="btn btn-danger btn-orange text-black pull-right" style="font-size: 14px;" @click="unapprove(listing.id)">Unapprove Artwork</a>
                                </div>
                                <div v-else class="col-6">
                                    <a v-if="listing.verified === 0" class="btn btn-danger btn-orange text-black pull-right" style="font-size: 14px;" @click="verify(listing.id)">Verify Artwork</a>
                                </div>
                            </div>
                        </div>
                        <div class="card-body">
                            <div class="row">
                                <div v-if="listing.image" class="col-6 mb-5">
                                    <div class="card">
                                        <div class="card-header">
                                            <p>{{ listing.image.split('/').pop() }}</p>
                                        </div>
                                        <div class="card-body">
                                            <img :src="listing.image" :alt="listing.name"/>
                                        </div>
                                    </div>
                                </div>
                                <div class="col mb-5 my-auto">
                                    <Dropzone name="dropzone" id="dropzone" ref="imageDropzone" 
                                            :options="imageDropzoneOptions" 
                                            :useCustomSlot=true 
                                            v-on:vdropzone-sending="appendFileName" 
                                            @vdropzone-file-added="fileInDropzone=true"
                                            @vdropzone-removed-file="fileInDropzone=false">
                                        <div class="dropzone-custom-content">
                                            <h5 class="dropzone-custom-title">Click here or drag and drop an image</h5>
                                            <div class="subtitle dropzone-custom-sub">accepted file types: png, jpg, jpeg <br><br>Recommended Aspect Ratio: <b>414:247</b></div>
                                        </div>
                                    </Dropzone>
                                    <button class="btn btn-primary" @click="uploadImage">Upload Image</button>
                                    <button v-if="fileInDropzone" class="btn btn-outline-danger ml-4" @click="removeImage">Clear Dropzone</button>
                                </div>
                            </div>
                            <Alert v-if="errorMessage.length > 0" :message="errorMessage" :error="true"/>
                            <Alert v-if="successMessage.length > 0" :message="successMessage" :error="false"/>

                            <!-- If the museum is null we are dealing with public art -->
                            <div v-if="listing.museum === null || listing.museum === undefined">
                                <div class="form-group">
                                    <div class="row mb-3">
                                        <div class="col">
                                            <label class="ml-2 text-dark" for="publicArtworkName">Artwork Name:</label>
                                            <input v-model="listing.name" id="publicArtworkName" type="text" placeholder="Artwork Name" class="form-control"/>
                                        </div>
                                    </div>
                                    <div class="row mb-4">
                                        <div class="col">
                                            <label class="ml-2 text-dark" for="publicArtworkYear">Year:</label>
                                            <input v-model="listing.year" id="publicArtworkYear" type="text" placeholder="* Year" class="form-control" maxlength="4"/>
                                        </div>
                                         <div class="col">
                                            <label class="ml-2 text-dark" for="publicArtworkArtist">Artist:</label>
                                            <select class="form-control custom-dropdown" id="publicArtworkArtist" v-model="listing.artist_id">

                                                <option v-for="artist in artists" :key="artist.id" :value="artist.id">{{artist.name}}</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div class="row mb-4">
                                        <div class="col">
                                            <label class="ml-2 text-dark" for="publicArtworkAddress">Address:</label>
                                            <input v-model="listing.address.address_1" id="publicArtworkAddress" type="text" class="form-control"/>
                                        </div>
                                        <div class="col">
                                            <label class="ml-2 text-dark" for="publicArtworkAddress2">Address 2:</label>
                                            <input v-model="listing.address.address_2" id="publicArtworkAddress2" type="text" class="form-control"/>
                                        </div>
                                    </div>
                                    <div class="row mb-4">
                                        <div class="col">
                                            <label class="ml-2 text-dark" for="publicArtworkCity">City:</label>
                                            <input v-model="listing.address.city" id="publicArtworkCity" type="text" class="form-control"/>
                                        </div>
                                        <div class="col">
                                            <label class="ml-2 text-dark" for="publicArtworkState">State/Province:</label>
                                            <input v-model="listing.address.state" id="publicArtworkState" type="text" class="form-control"/>
                                        </div>
                                    </div>
                                    <div class="row mb-4">
                                        <div class="col">
                                            <label class="ml-2 text-dark" for="publicArtworkCountry">Country:</label>
                                            <input v-model="listing.address.country" id="publicArtworkCountry" type="text" class="form-control"/>
                                        </div>
                                        <div class="col">
                                            <label class="ml-2 text-dark" for="publicArtworkPostalCode">Postal Code:</label>
                                            <input v-model="listing.address.postal_code" id="publicArtworkPostalCode" type="text" class="form-control"/>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-else>
                                <div class="form-group">
                                    <div class="row mb-3">
                                        <div class="col">
                                            <label class="ml-2 text-dark" for="artworkName">Artwork Name:</label>
                                            <input v-model="listing.name" id="artworkName" type="text" placeholder="* Artwork Name" class="form-control"/>
                                        </div>
                                        <div class="col">
                                            <label class="ml-2 text-dark" for="artworkEra">Era:</label>
                                            <select class="form-control custom-dropdown" id="artworkEra" v-model="listing.era_id">
                                                <option v-for="era in eras" :key="era.id" :value="era.id">{{era.name}}</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col">
                                            <label class="ml-2 text-dark" for="artworkYear">Year:</label>
                                            <input v-model="listing.year" id="artworkYear" type="text" placeholder="Year" class="form-control" maxlength="4"/>
                                        </div>
                                        <div class="col">
                                            <label class="ml-2 text-dark" for="artworkObjectNumber">Object Number:</label>
                                            <input v-model="listing.object_number" id="artworkObjectNumber" type="text" placeholder="Object Number" class="form-control"/>
                                        </div>
                                    </div>
                                    <div class="row mb-1">
                                        <div class="col my-auto ml-3">
                                            <div class="form-group my-auto">
                                                <label class="form-check-label"><input type="checkbox" v-model="listing.on_display"> On Display</label>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="row mb-1">
                                        <div class="col my-auto ml-3">
                                            <div class="form-group my-auto">
                                                <label class="form-check-label"><input type="checkbox" v-model="listing.circa"> Circa</label>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="row mb-3">
                                        <div class="col my-auto ml-3">
                                            <div class="form-group my-auto">
                                                <label class="form-check-label"><input type="checkbox" v-model="listing.BC"> BC</label>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="row mb-3">
                                        <div v-if="isSuperAdmin" class="col">
                                            <label class="ml-2 text-dark" for="artworkInstitute">Institute:</label>
                                            <select class="form-control custom-dropdown mb-5" id="artworkInstitute" v-model="listing.museum_id">
                                                <option v-for="museum in museums" :key="museum.id" :value="museum.id">{{museum.name}}</option>
                                            </select>
                                        </div>
                                        <div class="col">
                                            <label class="ml-2 text-dark" for="artworkMedium">Medium:</label>
                                            <select class="form-control custom-dropdown" id="artworkMedium" v-model="listing.medium_id">
                                                <option v-for="medium in mediums" :key="medium.id" :value="medium.id">{{medium.name}}</option>
                                            </select>
                                        </div>
                                        <div class="col">
                                            <label class="ml-2 text-dark" for="artworkArtist">Artist:</label>
                                            <select class="form-control custom-dropdown" id="artworkArtist" v-model="listing.artist_id">
                                                <option v-for="artist in artists" :key="artist.id" :value="artist.id">{{artist.name}}</option>
                                            </select>
                                            <button type="button" class="btn btn-primary btn-neutral btn-museo-link pull-right" @click="addNewArtist">Artist Not Listed</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <button class="btn btn-primary mt-5" @click="updateListing(listing)">{{ updateText }}</button>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>

        <NewArtistModal @artistCreated="artistCreated" />

    </div>
</template>

<script>
    import Dropzone from 'vue2-dropzone';
    import 'vue2-dropzone/dist/vue2Dropzone.min.css';

    import Mediums from '../controllers/medium.controller';
    import Eras from '../controllers/eras.controller';
    import Listings from '../controllers/listing.controller';
    import Museums from '../controllers/museum.controller';
    import Artists from '../controllers/artist.controller';
    import Addresses from '../controllers/addresses.controller';
    import isSuperAdmin from '../services/jwt/isSuperAdmin';

    export default {
        name: 'EditListing',
        components: {
            Dropzone
        },
        data: function() {
            return {
                // Image Dropzone
                imageDropzoneOptions: {
                    url: (process.env.VUE_APP_API_URL + '/listing/upload/' + this.$route.params.listingID) || "http://localhost:3001/api/listing/upload/" + this.$route.params.listingID,
                    maxFilesize: 50, // Mb
                    maxFiles: 1,
                    autoProcessQueue: false,
                    acceptedFiles: '.png,.jpg,.jpeg',
                    header: {
                        'Authorization': 'Bearer ' + localStorage.getItem('access_token')
                    }
                },
                listings: [],
                museums: [],
                mediums: [],
                eras: [],
                artists: [],
                listing: {
                    address: {
                        address_1: '',
                        address_2: '',
                        city: '',
                        state: '',
                        postal_code: ''
                    }
                },
                fileInDropzone: false,
                loading: false,
                errorMessage: '',
                successMessage: '',
                isSuperAdmin: isSuperAdmin(localStorage.getItem('superAdmin')),
                updateText: isSuperAdmin(localStorage.getItem('superAdmin')) ? "Update Artwork" : "Update & Verify Artwork"
            }
            
        },

        beforeMount: async function() {
            this.loading = true;

            if (!this.$route.params.listingID) {
                await this.$router.push({ name: 'Listings' });
            }
            
            // Fetch the info for the listingID from the API
            this.listing = await Listings.getByID(this.$route.params.listingID);

            // Fetch all the information that we need for the view
            if (this.isSuperAdmin === true) {
                this.museums = await Museums.getAll();
            }

            if (this.listing.museum != null) {
                this.mediums = await Mediums.getAll();
                this.eras = await Eras.getAll();
            }

            await this.getArtists();

            this.loading = false;
        },

        methods: {

            async getArtists() {
                let noArtist = [{
                    id: null,
                    name: "No Artist"
                }]
                let artist = await Artists.getAll();
                this.artists = noArtist.concat(artist);
            },

            addNewArtist() {
                this.$modal.show('new-artist-modal')
            },

            async artistCreated(event) {
                this.loading = true;

                this.listing.artist_id = event.artistID == null ? '' : event.artistID;
                await this.getArtists();

                this.loading = false;
            },

            async updateListing(listing) {
                // Reset the alert messages
                this.successMessage = '';
                this.errorMessage = '';

                if (listing.museum != null) {
                    if (!listing.name || !listing.medium_id) {
                        this.errorMessage = 'Fill all mandatory fields';
                        return;
                    }   
                } else {
                    if (!listing.address.address_1 || !listing.address.city || !listing.address.country) {
                        this.errorMessage = 'Fill all mandatory fields';
                        return;
                    }   
                }

                if (listing.year !== null) {
                    if (isNaN(listing.year)) {
                        this.errorMessage = 'Year field must be a number';
                        return;
                    } else {
                        listing.year = parseInt(listing.year, 10);
                    }
                }

                this.loading = true;

                // If this is public art, lets update its address
                if (listing.museum == null) {
                    if (listing.address == null) {
                        listing.address_id = await Addresses.createNew(listing.address.address_1, listing.address.city, listing.address.state, listing.address.postal_code, listing.address.country, listing.address.address_2);
                    } else {
                        listing.address_id = await Addresses.update(listing.address_id, listing.address.address_1, listing.address.city, listing.address.state, listing.address.postal_code, listing.address.country, listing.address.address_2);
                    }

                    if (listing.address_id.error || listing.address_id === false) {
                        this.errorMessage = "Something went wrong while updating the address of the artwork.";
                        return;
                    }
                }

                let updated = await Listings.updateExisting(listing);

                if (updated === false) {
                    this.errorMessage = 'Something went wrong while updating your artwork.';
                } else if (updated.error) {
                    this.errorMessage = updated.error;
                } else {
                    // Approve only if the listing is unverified
                    // Only curators should be able to verify
                    if (this.isSuperAdmin === false && this.listing.verified === 0) {
                        let verified = await Listings.verify(this.listing.id);
                        if (verified === false) {
                            this.errorMessage = "Something went wrong while updating the artwork.";
                            return;
                        } else if(verified.error) {
                            this.errorMessage = verified.error;
                            return;
                        }
                    }

                    // Approve only if the listing is unapproved
                    if (this.listing.approved === 0) {
                        let approved = await Listings.approve(this.listing.id);
                        if (approved === false) {
                            this.errorMessage = "Something went wrong while updating the artwork.";
                            return;
                        } else if(approved.error) {
                            this.errorMessage = approved.error;
                            return;
                        }
                    }

                    this.successMessage = "Artwork updated successfully";
                    this.listing = await Listings.getByID(this.listing.id);
                }

                this.loading = false;
            },

            async verify() {
                this.loading = true;
                let verified = await Listings.verify(this.listing.id);

                if (verified === true) {
                    this.successMessage = "Artwork verified successfully!";
                    this.listing = await Listings.getByID(this.listing.id);
                } else if(verified.error) {
                    this.errorMessage = verified.error;
                } else {
                    this.errorMessage = "Error while verifying your artwork";
                }

                this.loading = false;
            },

            async unverify() {
                this.loading = true;
                let unverified = await Listings.unverify(this.listing.id);

                if (unverified === true) {
                    this.successMessage = "Artwork unverified successfully!";
                    this.listing = await Listings.getByID(this.listing.id);
                } else if(unverified.error) {
                    this.errorMessage = unverified.error;
                } else {
                    this.errorMessage = "Error while unverifying your artwork";
                }

                this.loading = false;
            },

            async unapprove() {
                this.loading = true;
                let unverified = await Listings.unapprove(this.listing.id);

                if (unverified === true) {
                    this.successMessage = "Artwork unapproved successfully!";
                    this.listing = await Listings.getByID(this.listing.id);
                } else if(unverified.error) {
                    this.errorMessage = unverified.error;
                } else {
                    this.errorMessage = "Error while unapproving your artwork";
                }

                this.loading = false;
            },

            uploadImage() {
                this.$refs.imageDropzone.processQueue();
            },

            resetForm() {
                this.errorMessage = '';
                this.listing = {};
                this.$refs.imageDropzone.removeAllFiles(true);
            },

            removeImage() {
                this.$refs.imageDropzone.removeAllFiles(true);
            },
            
            async appendFileName(file, xhr, formData) {
                file.upload.filename = Date.now() + file.upload.filename.slice(file.upload.filename.lastIndexOf('.'), file.upload.filename.length);
                let uploaded = await Listings.uploadImage(this.$route.params.listingID, file.upload.filename, file.dataURL);
                if (uploaded === true) {
                    this.successMessage = "Image uploaded successfully";
                    this.listing = await Listings.getByID(this.$route.params.listingID);
                    this.$refs.imageDropzone.removeAllFiles(true);
                } else {
                    this.errorMessage = "Something went wrong with uploading your image"
                }

            },

            async deleteListing(listingID) {
                if (window.confirm("Are you sure you want to delete this listing? This action cannot be undone.")) {
                    let deleted = await Listings.delete(listingID);

                    if (deleted === true) {
                        await this.$router.push({ name: 'Listings' });
                    } else {
                        this.errorMessage = 'Something went wrong with deleting the listing';
                    }
                }
            }

        }
    }
</script>